import { ArrowBack } from '@mui/icons-material';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CountryPhoneInput from '../elements/common/CountryPhoneInput';
import InputField from '../elements/InputField';
import { STEPS, INDUSTRY_OPTIONS } from '../../static-data/DropdownOptions';

/* ---- Step Indicator -------------------------------------------------------- */
export const StepIndicator = ({ activeStep }) => (
  <Stack direction="row" alignItems="flex-start" sx={{ mb: 3 }}>
    {STEPS.map((step, idx) => {
      const isCompleted = idx < activeStep;
      const isActive    = idx === activeStep;
      const Icon        = step.icon;

      return (
        <Stack
          key={idx}
          direction="row"
          alignItems="center"
          sx={{ flex: idx < STEPS.length - 1 ? 1 : 'unset' }}
        >
          <Stack alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 38, height: 38,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: isCompleted ? 'success.main' : isActive ? 'primary.main' : 'grey.200',
                color: isCompleted || isActive ? 'primary.contrastText' : 'text.disabled',
                transition: 'background-color 0.2s ease',
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: isActive ? 600 : 400,
                color: isActive ? 'primary.main' : isCompleted ? 'success.main' : 'text.disabled',
                whiteSpace: 'nowrap',
              }}
            >
              {step.label}
            </Typography>
          </Stack>

          {idx < STEPS.length - 1 && (
            <Box
              sx={{
                flex: 1, height: 2, mx: 1.5, mb: 3,
                bgcolor: idx < activeStep ? 'success.main' : 'grey.200',
                transition: 'background-color 0.3s ease',
              }}
            />
          )}
        </Stack>
      );
    })}
  </Stack>
);

/* ---- Form Header ----------------------------------------------------------- */
export const OrganizationFormHeader = ({ activeStep, isSubmitting, onBack }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
    <IconButton size="small" disabled={isSubmitting} aria-label="back" onClick={onBack}>
      <ArrowBack fontSize="small" />
    </IconButton>
    <Stack spacing={0}>
      <Typography variant="h5">Set Up Your Organization</Typography>
      <Typography variant="body2" color="text.secondary">
        Step {activeStep + 1} of {STEPS.length} -- {STEPS[activeStep].sublabel}
      </Typography>
    </Stack>
  </Stack>
);

/* ---- Step 1: Organization Details ----------------------------------------- */
export const StepOne = ({ values, errors, touched, handleBlur, handleChange, setFieldValue }) => {
  const industryError = Boolean(touched.industry && errors.industry);

  return (
    <Grid container spacing={2.5} alignItems="flex-start">

      <Grid item xs={12}>
        <Stack spacing={0.25}>
          <Typography variant="h6">Company Information</Typography>
          <Typography variant="body2" color="text.secondary">
            Tell us about your organization.
          </Typography>
        </Stack>
      </Grid>

      {/* Organization Name */}
      <Grid item xs={12}>
        <InputField
          label="Name *"
          id="organization"
          name="organization"
          fullWidth
          placeholder="Acme Corp"
          value={values.organization}
          error={Boolean(touched.organization && errors.organization)}
          helperText={touched.organization && errors.organization}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

    <Grid item xs={12} sm={6}>
      <Box>
        {/* Label above the box — same as InputField / CountryPhoneInput */}
        <Typography
          variant="body2"
          component="label"
          htmlFor="industry"
          sx={{
            display: 'block',
            mb: 0.75,
            fontSize: '0.8125rem',
            color: industryError ? 'error.main' : 'text.secondary',
            fontFamily: 'inherit',
          }}
        >
          Industry
        </Typography>

        <Select
            id="industry"
            name="industry"
            value={values.industry}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            displayEmpty
            error={industryError}
            input={<OutlinedInput />}
            MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}
          >
          <MenuItem value="" disabled>
            <Typography color="text.disabled">Select an industry</Typography>
          </MenuItem>
          {INDUSTRY_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>

        {industryError && (
          <FormHelperText error sx={{ mx: '14px' }}>
            {errors.industry}
          </FormHelperText>
        )}
      </Box>

      {values.industry === 'Other' && (
        <Box sx={{ mt: 2 }}>
          <InputField
            label="Please specify"
            id="industry_other"
            name="industry_other"
            fullWidth
            placeholder="e.g. Logistics, Legal, Agriculture"
            value={values.industry_other}
            error={Boolean(touched.industry_other && errors.industry_other)}
            helperText={touched.industry_other && errors.industry_other}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Box>
      )}
    </Grid>

      {/* CountryPhoneInput already renders a floating label inside the border */}
      <Grid item xs={12} sm={6}>
        <CountryPhoneInput
        countryCode={values.orgCountryCode}     
        phoneNumber={values.orgPhoneNumber}     
        label="Phone Number"
        error={Boolean(touched.orgPhoneNumber && errors.orgPhoneNumber)}
        helperText={touched.orgPhoneNumber && errors.orgPhoneNumber}
        onChange={(field, value) => {

          if (field === 'countryCode') setFieldValue('orgCountryCode', value);
          else if (field === 'phoneNumber') setFieldValue('orgPhoneNumber', value);
        }}
       />
      </Grid>

      {/* Website + LinkedIn — same pattern, already aligned */}
      <Grid item xs={12} sm={6}>
        <InputField
          label="Website *"
          id="website"
          name="website"
          type="url"
          fullWidth
          placeholder="https://yourcompany.com"
          value={values.website}
          error={Boolean(touched.website && errors.website)}
          helperText={touched.website && errors.website}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <InputField
          label="LinkedIn Page URL"
          id="linkedin"
          name="linkedin"
          type="url"
          fullWidth
          placeholder="https://linkedin.com/company/acme"
          value={values.linkedin}
          error={Boolean(touched.linkedin && errors.linkedin)}
          helperText={touched.linkedin && errors.linkedin ? errors.linkedin : 'Optional'}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

    </Grid>
  );
};

/* ---- Step 2: Why GAiNSS ---------------------------------------------------- */
export const StepTwo = ({ values, errors, touched, handleBlur, handleChange, isSubmitting }) => (
  <Grid container spacing={2.5}>
    <Grid item xs={12}>
      <Stack spacing={0.25}>
        <Typography variant="h6">Why are you evaluating GAiNSS?</Typography>
        <Typography variant="body2" color="text.secondary">
          This helps us tailor your onboarding experience. You can skip this for now.
        </Typography>
      </Stack>
    </Grid>

    <Grid item xs={12}>
      <InputField
        label="Tell us about your QA challenges"
        id="evaluationReason"
        name="evaluationReason"
        fullWidth
        multiline
        rows={5}
        placeholder="Describe the challenges you face, your current test process, and what you hope to achieve"
        value={values.evaluationReason}
        error={Boolean(touched.evaluationReason && errors.evaluationReason)}
        helperText={
          touched.evaluationReason && errors.evaluationReason
            ? errors.evaluationReason
            : `${values.evaluationReason.length} / 1000  -  Optional`
        }
        onBlur={handleBlur}
        onChange={handleChange}
        inputProps={{ maxLength: 1000 }}
      />
    </Grid>

    <Grid item xs={12}>
      <Typography variant="body2" color="text.secondary">
        By creating an account you agree to our&nbsp;
        <Link variant="body2" component={RouterLink} target="_blank" color="primary"
          to="https://www.gtentechnologies.com/termsandconditions/">
          Terms of Service
        </Link>
        &nbsp;and&nbsp;
        <Link variant="body2" component={RouterLink} target="_blank" color="primary"
          to="https://www.gtentechnologies.com/privacypolicy/">
          Privacy Policy
        </Link>.
      </Typography>
    </Grid>

    {errors.submit && (
      <Grid item xs={12}>
        <Typography variant="body2" color="error.main">{errors.submit}</Typography>
      </Grid>
    )}

    <Grid item xs={12}>
      <Button
        disableElevation disabled={isSubmitting}
        fullWidth size="large" type="submit" variant="contained" color="primary"
      >
        {isSubmitting ? 'Creating Account...' : 'Create Account'}
      </Button>
    </Grid>
  </Grid>
);